import React from 'react';
import Modal from 'react-bootstrap/Modal';
import { Row, Col, Form } from 'react-bootstrap';

// Services & Helpers
import API from 'API';
import FormHelper from 'helpers/FormHelper';
import TextHelpers from 'helpers/TextHelpers';

// Components
import Loader from 'components/common/Loader';
import CurrencyInput from 'components/common/CurrencyInput';

//-----------------------------------------------------------------

class EditAccountModal extends React.Component {

    constructor(props) {
        super(props);

        this.state = {};
        this.form = new FormHelper({
            fields: {
                name: {
                    label: 'Name',
                    type: 'text'
                },
                territoryNumber: {
                    label: 'Territory Number'
                },
                isBookingAgent: {
                    boxLabel: 'Booking agent',
                    type: 'checkbox'
                },
                isTrialMode: {
                    boxLabel: 'Trial mode (disable outgoing comms)',
                    type: 'checkbox'
                },
                useItineroForRouting: {
                    boxLabel: 'Use Itinero for routing',
                    type: 'checkbox'
                },
                itineroRouterDBName: {
                    label: 'Itinero RouterDB name'
                },
                qbCustomerID: {
                    label: 'Quickbooks Customer',
                    type: 'single-select',
                    blankText: '(Not Selected)',
                    isReadOnly: () => this.state.financialsLocked,
                    getOptions: () => 
                        this.state.qbCustomers.map(c => ({
                            value: c.id,
                            text: c.name
                        }))
                },
                qbTermsID: {
                    label: 'Terms',
                    type: 'single-select',
                    blankText: '(Not Selected)',
                    isReadOnly: () => this.state.financialsLocked,
                    getOptions: () =>
                        this.state.qbTerms.map(c => ({
                            value: c.id,
                            text: c.name
                        }))
                },
                accountInvoiceDayOfMonth: {
                    label: 'Invoice on (Day of Month)',
                    type: 'number',
                    min: 1,
                    max: 28,
                    isReadOnly: () => this.state.financialsLocked
                }
            },
            getValue: (fieldName) => this.state.account[fieldName],
            setValue: (fieldName, value) => this.updateFields({ [fieldName]: value })
        });
    }

    open(opt) {
        return new Promise((resolve) => {
            this.resolve = resolve;
            this.setState({
                isOpen: true,
                isLoading: true,
                id: opt.id,
                financialsLocked: true,
                qbCustomers: null,
                qbItems: null,
                qbTerms: null
            }, () => {
                this.load();
            });
        });
    }

    close() {
        this.setState({ isOpen: false });
    }

    async load() {
        return new Promise(async (resolve, reject) => {
            const { id } = this.state;
            let account;

            if (id) {
                this.setState({ isLoading: true });
                account = await API.call(`account/get/${id}`);
                if (!account) {
                    alert('Sorry, the account could not be found');
                    this.close();
                    return;
                }
            } else {
                account = {
                    recurringAccountInvoiceItems: []
                };
                this.setState({ financialsLocked: false });
            }

            // Load related items
            let qbCustomers, qbItems, qbTerms;
            const isQuickbooksConnected = await API.call('quickbooks/get-is-connected');
            if (isQuickbooksConnected) {
                const qbCustomersReq = API.call('quickbooks/list-customers');
                const qbItemsReq = API.call('quickbooks/list-items');
                const qbTermsReq = API.call('quickbooks/list-terms');
                qbItems = await qbItemsReq;
                qbCustomers = await qbCustomersReq;
                qbTerms = await qbTermsReq;
            }
            let accountInvoice;
            if (account.id) {
                accountInvoice = await API.call('account-invoice/get-latest/' + account.id);
            }

            // Update UI
            this.setState({
                isLoading: false,
                account,
                qbCustomers,
                qbItems,
                qbTerms,
                accountInvoice,
                isQuickbooksConnected
            });
        });
    }

    async save() {
        return new Promise(async (resolve) => {
            let { account } = this.state;
            this.setState({ isLoading: true });
            this.changedFields = {};
            const { id, code } = await API.call('head-office/save-account', account);
            this.setState({ isLoading: false });
            resolve({ action: 'save', id, code });
        });
    }

    async saveAndClose() {
        const result = await this.save();
        this.close();
        this.resolve(result);
    }

    async saveAndSetUp() {
        const { code } = await this.save();
        this.close();
        window.location = `/${code}/setup/account`;
    }

    updateFields(fields) {
        const account = { ...this.state.account };
        for (let fieldName in fields) {
            const value = fields[fieldName];
            account[fieldName] = value;
        }
        this.setState({ account });
    }

    updateRecurringAccountInvoiceItem(index, newValues) {
        const { account } = this.state;
        const recurringAccountInvoiceItems = [...account.recurringAccountInvoiceItems];
        const recurringAccountInvoiceItem = { ...recurringAccountInvoiceItems[index] };
        for (let fieldName in newValues) {
            const value = newValues[fieldName];
            recurringAccountInvoiceItem[fieldName] = value;
        }
        delete recurringAccountInvoiceItem.isNew;
        recurringAccountInvoiceItems[index] = recurringAccountInvoiceItem;
        this.updateFields({ recurringAccountInvoiceItems });
    }

    removeRecurringAccountInvoiceItem(index) {
        const { account } = this.state;
        const recurringAccountInvoiceItems = [...account.recurringAccountInvoiceItems];
        recurringAccountInvoiceItems.splice(index, 1);
        this.updateFields({ recurringAccountInvoiceItems });
    }

    async confirmDelete() {
        const confirm = window.confirm('Are you sure you want to delete this account?');
        if (confirm) {
            this.setState({ isLoading: true });
            await API.call('head-office/delete-account/' + this.state.account.id);
            this.close();
            this.resolve({ action: 'delete' });
        }
    }

    async confirmCreditAndReissueLatestInvoice() {
        const confirm = window.confirm('Are you sure you want to credit this invoice and raise a new one to replace it?');
        if (confirm) {
            this.setState({ isLoading: true });
            await API.call('account-invoice/credit-and-reisue/' + this.state.account.id);
            this.load();
        }
    }

    //--------------------------------------------------------------------------

    render() {
        const { 
            isOpen,
            isLoading,
            account
        } = this.state;

        if (!isOpen) {
            return null;
        }
       
        return (
            <Modal show onHide={() => this.close()} className="edit-account-modal" backdrop="static">
                <form onSubmit={e => { e.stopPropagation(); e.preventDefault(); this.saveAndClose() }}>
                    <Modal.Header>
                        <Modal.Title>Edit Account</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {this.renderInner()}
                    </Modal.Body>
                    {!isLoading &&
                        <Modal.Footer>

                            <button type="button" className="btn btn-secondary me-3" onClick={() => this.close()}>
                                Cancel
                            </button>

                            {!account.isHeadOffice &&
                                <button type="button" className="btn btn-danger" onClick={() => this.confirmDelete()}>
                                    Delete
                                </button>
                            }

                            <button type="button" className="btn btn-secondary ms-auto" onClick={() => this.saveAndClose()}>
                                <span className="icon fa fa-check" />{' '}Save & close
                            </button>

                            <button type="button" className="btn btn-primary" onClick={() => this.saveAndSetUp()}>
                                <span className="icon fa fa-chevron-right" />{' '}Save & set up
                            </button>

                        </Modal.Footer>
                    }
                </form>
            </Modal>
        );
    }

    renderInner() {
        const {
            isLoading,
            account,
            qbCustomers,
            qbTerms,
            accountInvoice,
            isQuickbooksConnected
        } = this.state;
        const { history } = this.props;

        if (isLoading) {
            return (<Loader />);
        }

        return (<>

            <h2>Account Details</h2>
            
            <Row>

                <Col md="8">

                    {this.form.render([
                        'name'
                    ])}

                </Col>

                {!account.isBookingAgent &&
                    <Col md="4">

                        {this.form.render([
                            'territoryNumber'
                        ])}

                    </Col>
                }

            </Row>

            {this.form.render([
                'isBookingAgent',
                'isTrialMode',
                'useItineroForRouting',
                account.useItineroForRouting && 'itineroRouterDBName'
            ])}

            {/*
            <h2>
                Financial

                <button type="button" className="btn btn-secondary btn-sm ms-auto" onClick={async () => {
                    const { financialsLocked } = this.state;
                    if (financialsLocked) {
                        const confirm = window.confirm('Unlock financial information for editing?');
                        if (!confirm) return;
                    }
                    this.setState({ financialsLocked: !financialsLocked });
                }}>
                    {this.state.financialsLocked ? <>
                        <span className="fa fa-lock" /> Unlock
                    </> : <>
                        <span className="fa fa-lock-open" /> Lock
                    </>}
                </button>
            </h2>

            {this.form.render([
                qbCustomers && 'qbCustomerID',
                'accountInvoiceDayOfMonth',
                qbTerms && 'qbTermsID'
            ])}
            */}

            {this.renderRecurringAccountInvoiceItemsTable()}

            {accountInvoice &&
                <div>
                    <h2>
                        Latest Invoice

                        <div className="ms-auto">
                            <a className="btn btn-primary" href={`https://app.qbo.intuit.com/app/invoice?txnId=${accountInvoice.qbInvoiceID}`} target="_blank" rel="noreferrer">
                                <span className="fa-solid fa-eye" />{' '}
                                View in QB
                            </a>

                            {isQuickbooksConnected &&
                                <button type="button" className="btn btn-primary ms-2" onClick={() => this.confirmCreditAndReissueLatestInvoice()}>
                                    <span className="fa-solid fa-eye" />{' '}
                                    Credit &amp; Reissue
                                </button>
                            }
                        </div>
                    </h2>
                    Value (Net): {TextHelpers.formatCurrency(accountInvoice.totalNet)}
                </div>
            }

        </>);
    }

    renderRecurringAccountInvoiceItemsTable() {
        const { account, financialsLocked, qbItems } = this.state;
        const recurringAccountInvoiceItems = [...account.recurringAccountInvoiceItems];
        if (!financialsLocked) {
            recurringAccountInvoiceItems.push({ isNew: true });
        }

        const renderItem = (raii, i) => {
            return (
                <tr key={i}>
                    <td>
                        <select
                            className="form-control"
                            value={raii.type || ''}
                            onChange={e => this.updateRecurringAccountInvoiceItem(i, { type: e.target.value })}
                            disabled={financialsLocked}
                            key={raii.isNew ? 'new' : i}
                        >
                            {raii.isNew &&
                                <option value="">(Select)</option>
                            }
                            <option value="FixedFee">Fixed Fee</option>
                            <option value="PercentageOfTurnover">% of Turnover</option>
                            <option value="RatePerRebookSMS">Rate per Rebook SMS</option>
                        </select>
                    </td>
                    {qbItems &&
                        <td>
                            {!raii.isNew &&
                                <select
                                    className="form-control"
                                    value={raii.qbItemID || ''}
                                    onChange={e => this.updateRecurringAccountInvoiceItem(i, { qbItemID: e.target.value })}
                                    disabled={financialsLocked}
                                >
                                    <option value="">(Select)</option>
                                    {qbItems.map(i =>
                                        <option key={i.id} value={i.id}>
                                            {i.name}
                                        </option>
                                    )}
                                </select>
                            }
                        </td>
                    }
                    <td>
                        {!raii.isNew &&
                            <input
                                type="text"
                                className="form-control"
                                value={raii.description}
                                onChange={e => this.updateRecurringAccountInvoiceItem(i, { description: e.target.value })}
                                disabled={financialsLocked}
                                autoFocus
                            />
                        }
                    </td>
                    <td className="amount-col">
                        {!raii.isNew && <>
                            {(raii.type == 'FixedFee' || raii.type == 'RatePerRebookSMS') ?
                                <CurrencyInput
                                    className="form-control"
                                    value={raii.amount || ''}
                                    onValueChange={(values) => this.updateRecurringAccountInvoiceItem(i, { amount: Number(values.value) || '' })}
                                    disabled={financialsLocked}
                                /> :
                                <div className="input-group">
                                    <input
                                        type="number"
                                        className="form-control"
                                        value={Math.round((raii.amount || 0) * 10000) / 100}
                                        onChange={e => this.updateRecurringAccountInvoiceItem(i, { amount: Number(e.target.value || 0) / 100 })}
                                        disabled={financialsLocked}
                                    />
                                    <span className="input-group-text">%</span>
                                </div>
                            }
                        </>}
                    </td>
                    {!financialsLocked &&
                        <td>
                            {!raii.isNew &&
                                <button type="button" className="btn btn-danger" onClick={() => this.removeRecurringAccountInvoiceItem(i)}>
                                    <span className="fa fa-times" />
                                </button>
                            }
                        </td>
                    }
                </tr>
            );
        };

        return (
            <Form.Group className="form-group">
                <Form.Label>Monthly Fees</Form.Label>
                
                <table className="table table-bordered recurring-account-invoice-items-table">
                    <thead>
                        <tr>
                            <th className="type-col">Type</th>
                            {qbItems &&
                                <th className="qb-item-col">QB Item</th>
                            }
                            <th className="description-col">Description</th>
                            <th className="amount-col">Amount</th>
                            {!financialsLocked &&
                                <th className="actions-col"></th>
                            }
                        </tr>
                    </thead>
                    <tbody>
                        {recurringAccountInvoiceItems.map((raii, i) => renderItem(raii, i))}
                    </tbody>
                </table>
            </Form.Group>
        );
    }
}

export default EditAccountModal;